import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatMoney } from '../../functions/utils';
import { Typography, Box } from '@mui/material';

const COLORS = ['#0F23DF', '#F0F0F0', '#07D2FE', '#2ECC71', '#E8C547'];

interface SemiPieChartProps {
  data: Array<{
    name: string;
    value: number;
  }>;
  money?: boolean;
  onClick?: () => void;
  width?: number;
  height?: number;
  cx?: number;
  cy?: number;
}

const CustomTooltip = ({ active, payload, money }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: 'white',
          padding: '8px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
        }}
      >
        <p style={{ margin: 0, fontSize: '14px' }}>{data.name}</p>
        <p style={{ margin: '4px 0 0 0', fontWeight: 'bold' }}>
          {money ? formatMoney(data.value) : data.value}
        </p>
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }: any) => (
  <Box display="flex" alignItems="center" gap={3} justifyContent="center">
    {payload.map((entry: any, index: number) => (
      <Box
        key={`legend-item-${index}`}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: entry.color,
            marginRight: 2,
          }}
        />
        <Typography variant="body2" fontWeight={400} color="text.secondary">
          {entry.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const SemiPieChart: React.FC<SemiPieChartProps> = ({
  data,
  money = false,
  width = 280,
  height = 200,
  cx = 140,
  cy = 100,
}) => {
  // Check if all values are 0
  const allValuesZero = data.every((item) => item.value === 0);

  // If all values are 0, create a single segment with 0 value to show empty chart
  const chartData = allValuesZero ? [{ name: 'Sin datos', value: 1 }] : data;

  return (
    <Box width={width} height={height}>
      <ResponsiveContainer>
        <>
          <PieChart width={width} height={height}>
            <Pie
              data={chartData}
              cx={cx}
              cy={cy}
              startAngle={0}
              endAngle={360}
              innerRadius={50}
              outerRadius={80}
              fill="#8884d8"
              paddingAngle={5}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    allValuesZero ? '#F0F0F0' : COLORS[index % COLORS.length]
                  }
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip money={money} />} />
          </PieChart>
          <Box mt={2}>
            <CustomLegend
              payload={
                allValuesZero
                  ? [{ value: 'Sin datos', color: '#F0F0F0' }]
                  : data.map((d, i) => ({
                      value: d.name,
                      color: COLORS[i % COLORS.length],
                    }))
              }
            />
          </Box>
        </>
      </ResponsiveContainer>
    </Box>
  );
};

export default SemiPieChart;
